import React from "react";
import { Box, Typography } from "@mui/material";

export function formatScore(v) {
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}`;
}

// Severity framing for an explanation — computed purely from the two scores
// already known client-side (no LLM involved). Crossing zero (net positive
// <-> net negative) is treated as the headline signal since that's a
// qualitatively different story than two numbers moving while staying on the
// same side of neutral.
export function scoreSeverity(fromScore, toScore) {
  const fromSign = fromScore > 0 ? 1 : fromScore < 0 ? -1 : 0;
  const toSign = toScore > 0 ? 1 : toScore < 0 ? -1 : 0;
  if (fromSign !== 0 && toSign !== 0 && fromSign !== toSign) {
    return toSign > 0
      ? { label: "Flipped positive", color: "#1b7f4d", bg: "#e6f4ec" }
      : { label: "Flipped negative", color: "#c0352b", bg: "#fbe9e7" };
  }
  return toScore > fromScore
    ? { label: "Improved", color: "#1b7f4d", bg: "#e6f4ec" }
    : { label: "Declined", color: "#c0352b", bg: "#fbe9e7" };
}

export function SeverityBadge({ fromScore, toScore }) {
  const severity = scoreSeverity(fromScore, toScore);
  return (
    <Box sx={{ display: "inline-block", fontSize: 11, fontWeight: 700, color: severity.color, bgcolor: severity.bg, px: 0.9, py: 0.2, borderRadius: "10px" }}>
      {severity.label}
    </Box>
  );
}

/**
 * Shared body for a trend explanation object — grouped themes (New/
 * Intensified/Faded, or Worsened/Improved for Overall) plus the "Why"
 * analysis bullets. Used both inside TrendChart's click-to-explain modal and
 * DetailView's "What Changed Since Last Run" section, so the two surfaces
 * never drift out of sync. `explanation` is the shape trendExplanation.js
 * (backend) produces: {fromScore,toScore,fromDate,toDate,groups,analysis}.
 */
export default function TrendExplanationDetail({ explanation }) {
  return (
    <>
      {explanation.groups?.map((group) => (
        <Box key={group.label} sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
            {group.label}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {group.items.map((item, i) => (
              <Box component="li" key={i} sx={{ fontSize: 13.5, lineHeight: 1.6, color: "#344054", mb: 0.5 }}>
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
        Why
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
        {/* analysis is an array of short bullets; defensively wrap a stray
            plain string (old pre-backfill data) as a single bullet */}
        {(Array.isArray(explanation.analysis) ? explanation.analysis : [explanation.analysis]).map((point, i) => (
          <Box component="li" key={i} sx={{ fontSize: 13.5, lineHeight: 1.6, color: "#344054", mb: 0.5 }}>
            {point}
          </Box>
        ))}
      </Box>
    </>
  );
}
